import React, { useEffect, useRef } from 'react';
import { Zap, Truck, Calendar, Users } from 'lucide-react';
import { solutions } from '../data/mockData';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  Zap: Zap,
  Truck: Truck,
  Calendar: Calendar,
  Users: Users,
};

const SolutionSection = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(titleRef.current,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards stagger animation
      const cards = cardsRef.current?.querySelectorAll('.solution-card');
      if (cards) {
        gsap.fromTo(cards,
          { y: 80, opacity: 0, rotationX: 20 },
          {
            y: 0,
            opacity: 1,
            rotationX: 0,
            duration: 0.7,
            stagger: 0.15,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="solution" className="py-24 bg-[#0A1628] relative overflow-hidden" ref={sectionRef}>
      {/* Decorative diagonal shape */}
      <div className="absolute top-0 left-0 w-24 h-full bg-[#00A86B]/10 transform -skew-x-12 -translate-x-12"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header - 20% larger */}
        <div ref={titleRef} className="text-center mb-20">
          <span className="text-[#00A86B] font-bold text-base tracking-wider">THE SOLUTION</span>
          <h2 className="text-5xl md:text-6xl font-black text-white mt-5 mb-3">
            CAPITAL THAT
          </h2>
          <h2 className="text-5xl md:text-6xl font-black text-[#00A86B] mb-8">
            MOVES LIKE YOU
          </h2>
          <div className="w-20 h-1.5 bg-[#00A86B] mx-auto"></div>
        </div>

        {/* Solution Cards - 20% larger */}
        <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {solutions.map((solution, index) => {
            const IconComponent = iconMap[solution.icon];
            return (
              <div 
                key={index} 
                className="solution-card bg-[#0f1f35] p-8 rounded-xl cursor-pointer relative overflow-hidden transition-all duration-300 hover:bg-[#152744] group border-2 border-transparent hover:border-[#00A86B]"
              >
                {/* Corner peel tab */}
                <div className="absolute top-0 right-0 w-0 h-0 border-l-[40px] border-l-transparent border-t-[40px] border-t-[#00A86B] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <h3 className="text-4xl md:text-5xl font-black text-[#00A86B] mb-5">
                  {solution.metric}
                </h3>
                <div className="w-12 h-12 bg-[#0A1628] rounded-lg flex items-center justify-center mb-5 group-hover:bg-[#00A86B]/20 transition-colors">
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-white text-xl mb-3">{solution.title}</h4>
                <p className="text-gray-400 text-base">{solution.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
